import React, { createContext, useState, useEffect } from 'react'
import {
  CURRENT_YEAR,
  hasChampSeriesStarted,
  STANDINGS_YEAR,
} from '../../Config/League/league.config'
import { AppConfig, getAppConfig } from '../../Api'
import {
  STORAGE_NAMES,
  SEASON_SELECTOR_TYPES,
  SeasonSelectorType,
} from '../../Constant'
import { useStorageContext } from '../StorageContext'
import { SEASON_SEGMENT_NAMES } from '../../Utils'

export type SeasonType = 'regular' | 'post' | 'allstar' | 'champseries'
export type SeasonSelectors = { [key: string]: { seasonType: SeasonType; year: number } }
export type Season = { seasonType: SeasonType; year: number }
type SeasonContextType = {
  appConfig: AppConfig | null
  incompleteMarkets: string[]
  seasonSelect: SeasonSelectors
  onSeasonDropdownChange: (seasonOpts: Season, type?: SeasonSelectorType) => void
}

export const SeasonContext = createContext<SeasonContextType | null>(null)

export const SeasonProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const startingSeasonType = !hasChampSeriesStarted
    ? SEASON_SEGMENT_NAMES.regular
    : SEASON_SEGMENT_NAMES.champseries
  const [seasonSelect, setSeasonSelect] = useState<SeasonSelectors>({
    main: {
      seasonType: startingSeasonType,
      year: CURRENT_YEAR,
    },
    standings: {
      seasonType: startingSeasonType,
      year: STANDINGS_YEAR,
    },
  })
  const { isStorageReady, fetchAndCache } = useStorageContext()
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null)
  const [incompleteMarkets, setIncompleteMarkets] = useState<string[]>([])

  useEffect(() => {
    if (isStorageReady) {
      fetchNewConfig()
    }
  }, [isStorageReady])

  const fetchNewConfig = async () => {
    const fetcher = async (): Promise<AppConfig> => {
      const configRes = await getAppConfig(null)
      if (!configRes?.appConfig) {
        throw new Error('No app config available')
      }
      return configRes.appConfig
    }

    const onUpdate = (config: AppConfig) => {
      if (config) {
        handleSettingConfigData(config)
      }
    }

    try {
      await fetchAndCache<AppConfig>(STORAGE_NAMES.app_config, fetcher, onUpdate)
    } catch (error) {
      console.error('Error fetching app config:', error)
    }
  }

  const handleSettingConfigData = (config: AppConfig) => {
    setAppConfig(config)
    if (config?.incompleteMarkets) setIncompleteMarkets(config.incompleteMarkets)
  }

  const onSeasonDropdownChange = async (
    seasonOpts: Season,
    type: SeasonSelectorType = SEASON_SELECTOR_TYPES.MAIN
  ) => {
    setSeasonSelect((prevSeasonSelect) => ({
      ...prevSeasonSelect,
      [type]: seasonOpts,
    }))
  }

  return (
    <SeasonContext.Provider
      value={{
        appConfig,
        incompleteMarkets,
        seasonSelect,
        onSeasonDropdownChange,
      }}
    >
      {children}
    </SeasonContext.Provider>
  )
}
